/**
 * Central route definitions and path parsing for the omni-app.
 * All path matching and param extraction for the app shell happens here.
 * See AGENT.md "Routing" for the full route table.
 */

export type AppMode = 'chat' | 'cowork';

export type CurrentPage =
  | 'settings'
  | 'team'
  | 'dev-mode';

export interface AppRouteParams {
  activeMode: AppMode;
  currentPage: CurrentPage;
}

/**
 * Parses the app pathname (no leading slash) and returns activeMode, currentPage, and any object IDs.
 * Used by App to derive state from URL. Unknown paths resolve to cowork mode.
 */
export function parseAppPath(pathname: string): AppRouteParams {
  const path = pathname.startsWith('/') ? pathname.slice(1) : pathname;
  const segments = path ? path.split('/').filter(Boolean) : [];

  // Mode-level
  if (segments[0] === 'chat') {
    return { activeMode: 'chat', currentPage: 'settings' };
  }

  // Settings / team / dev-mode routes
  switch (segments[0]) {
    case 'settings':
      return { activeMode: 'cowork', currentPage: 'settings' };
    case 'team':
      return { activeMode: 'cowork', currentPage: 'team' };
    case 'dev-mode':
      return { activeMode: 'cowork', currentPage: 'dev-mode' };
    default:
      return { activeMode: 'cowork', currentPage: 'settings' };
  }
}

/**
 * Returns the path string for a given page.
 * Used for programmatic navigation (navigate(getPathForPage(...))).
 */
export function getPathForPage(page: CurrentPage): string {
  const map: Record<CurrentPage, string> = {
    settings: '/settings',
    team: '/team',
    'dev-mode': '/dev-mode',
  };
  return map[page] ?? '/';
}

/** Path for mode-only (chat/cowork). */
export function getPathForMode(mode: AppMode): string {
  if (mode === 'chat') return '/chat';
  return '/cowork';
}
